import { fetchFromBrandedUrl } from "@/api/utils";
import { brandUrl } from "@/api/utils";
import { EnvironmentVariables } from "@/modules/common/types";
import { AxiosRequestConfigMethods } from "@/api/types";
import {
  AgentTargetDTO,
  CreateExtraAchievementDTO,
  ExtraAchievementDTO,
  MemberHierarchyResponseData,
  QueryAgentTargetsDTO,
  QueryExtraAchievementsDTO,
  QueryUsersWithTargetsDTO,
  TargetUserDTO,
  TreeInitialNodesResponseData,
} from "@/modules/agents/dtos/index.v2";
import { AxiosResponse } from "axios";
import { UpdateUserConfig } from "@/modules/common/dtos/index.v2";

export const createExtraAchievement = async ({
  payload,
}: {
  payload: CreateExtraAchievementDTO;
}) => {
  const apiUrl = brandUrl(`${EnvironmentVariables.MIDAS_API}/extraachievement`);

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: payload,
    },
  });
};

export const getTargets = async ({
  signal,
  query,
}: {
  signal?: AbortSignal;
  query: Omit<QueryAgentTargetsDTO, "organizationUId">;
}): Promise<AxiosResponse<AgentTargetDTO[]>> => {
  const apiUrl = brandUrl<AgentTargetDTO[]>(
    `${EnvironmentVariables.MIDAS_API}/targets`
  );

  return fetchFromBrandedUrl({
    options: {
      signal,
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...query,
      },
      includeSingleOrganization: true,
    },
  });
};

export const getExtraAchievments = async ({
  signal,
  query,
}: {
  signal?: AbortSignal;
  query: QueryExtraAchievementsDTO;
}): Promise<AxiosResponse<ExtraAchievementDTO[]>> => {
  const apiUrl = brandUrl<ExtraAchievementDTO[]>(
    `${EnvironmentVariables.MIDAS_API}/extraachievement`
  );

  return fetchFromBrandedUrl({
    options: {
      signal,
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...query,
      },
      // includeOrganizationUid: true,
    },
  });
};

export const deleteExtraAchievments = async ({
  id,
}: {
  id: number;
}): Promise<AxiosResponse<ExtraAchievementDTO[]>> => {
  const apiUrl = brandUrl<ExtraAchievementDTO[]>(
    `${EnvironmentVariables.MIDAS_API}/extraachievement/${id}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
      // includeOrganizationUid: true,
    },
  });
};

export const getMemberHierarchy = async (userUId: string) => {
  const apiUrl = brandUrl<MemberHierarchyResponseData>(
    `${EnvironmentVariables.UMS_API_URL}/members/hierarchy/${userUId}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<MemberHierarchyResponseData>;
};

export const getInitialNodes = async (organizationUid: string) => {
  const apiUrl = brandUrl<TreeInitialNodesResponseData>(
    `${EnvironmentVariables.UMS_API_URL}/organizations/${organizationUid}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      includeSingleOrganization: true,
    },
  }) as Promise<TreeInitialNodesResponseData>;
};

export const getUsersWithTargets = async ({
  signal,
  query,
}: {
  signal?: AbortSignal;
  query: QueryUsersWithTargetsDTO;
}): Promise<AxiosResponse<TargetUserDTO[]>> => {
  const apiUrl = brandUrl<TargetUserDTO[]>(
    `${EnvironmentVariables.MIDAS_API}/targets/userswithtargets`
  );

  return fetchFromBrandedUrl({
    options: {
      signal,
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...query,
        name: query.name ? query.name : "a",
      },
      includeSingleOrganization: true,
    },
  });
};

export const updateUserConfig = async (
  userId: string | undefined,
  config: UpdateUserConfig
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/user-configurations/${userId}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: config,
    },
  });
};

export const removeMultipleBlockedLocations = async (
  userId: string,
  locationUIds: string[]
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/users/${userId}/blocked-locations`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
      data: { locationUIds },
    },
  });
};

export const addMultipleBlockedLocations = async (
  userId: string,
  locationUIds: string[]
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/users/${userId}/blocked-locations`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { locationUIds },
    },
  });
};
